"use client"

import { useState } from "react"
import { ChevronDown, Dot, LucideIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { P } from "@/components/ui/p"
import { DropdownMenuArrow } from "@radix-ui/react-dropdown-menu"
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger
} from "@/components/ui/collapsible"
import {
    Tooltip,
    TooltipTrigger,
    TooltipContent,
    TooltipProvider
} from "@/components/ui/tooltip"
import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"

type Submenu = {
    href: string
    label: string
    active?: boolean
}

interface CollapseMenuButtonProps {
    icon: LucideIcon
    label: string
    active: boolean
    submenus: Submenu[]
    isOpen: boolean | undefined
    href: string
    onHover?: (rect: DOMRect) => void
    onHoverEnd?: () => void
}

export function CollapseMenuButton({
    icon: Icon,
    label,
    active,
    submenus,
    isOpen,
    href,
    onHover,
    onHoverEnd
}: CollapseMenuButtonProps) {
    const pathname = usePathname()
    const router = useRouter()
    const isSubmenuActive = submenus.some((submenu) =>
        submenu.active === undefined ? submenu.href === pathname : submenu.active
    )
    // Default to collapsed state instead of expanded
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false)

    const handleClick = () => {
        if (submenus.length === 0) {
            router.push(href)
        } else {
            setIsCollapsed(!isCollapsed)
        }
    }

    return isOpen ? (
        <Collapsible
            open={isCollapsed}
            onOpenChange={setIsCollapsed}
            className="w-full"
        >
            <CollapsibleTrigger
                className="[&[data-state=open]>div>div>svg]:rotate-0 [&[data-state=closed]>div>div>svg]:-rotate-90 mb-0.5 z-[20002]"
                asChild
            >
                <Button
                    variant={'ghost'}
                    className="w-full items-center h-8 pl-2 pr-2 justify-start hover:bg-transparent"
                    onClick={handleClick}
                    onMouseEnter={(e) => onHover?.(e.currentTarget.getBoundingClientRect())}
                    onMouseMove={(e) => onHover?.(e.currentTarget.getBoundingClientRect())}
                    onMouseLeave={() => onHoverEnd?.()}
                >
                    <div className="w-full items-center flex justify-between">
                        <div className="flex items-center">
                            <span className="mr-2">
                                <Icon size={18} />
                            </span>
                            <p className="max-w-[150px] truncate font-normal">
                                {label}
                            </p>
                        </div>
                        {submenus.length > 0 && <div className="whitespace-nowrap">
                            <ChevronDown
                                size={18}
                                className="transition-transform duration-200"
                            />
                        </div>}
                    </div>
                </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-hidden hover:bg-transparent data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                {submenus.map(({ href, label, active }, index) => (
                    <Button
                        key={index}
                        variant={'ghost'}
                        className="h-7 pl-1 justify-start items-center mb-0.5 ml-4 rounded-md group w-full"
                        asChild
                        onMouseEnter={(e) => onHover?.(e.currentTarget.getBoundingClientRect())}
                        onMouseMove={(e) => onHover?.(e.currentTarget.getBoundingClientRect())}
                        onMouseLeave={() => onHoverEnd?.()}
                    >
                        <P className="font-normal text-xs" speed={60} href={href}>
                            {label}
                        </P>
                    </Button>
                ))}
            </CollapsibleContent>
        </Collapsible>
    ) : (
        <TooltipProvider disableHoverableContent>
            <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                    <Button
                        variant={'ghost'}
                        className="w-8 h-8 justify-center mb-0.5"
                        asChild
                        onMouseEnter={(e) => onHover?.(e.currentTarget.getBoundingClientRect())}
                        onMouseMove={(e) => onHover?.(e.currentTarget.getBoundingClientRect())}
                        onMouseLeave={() => onHoverEnd?.()}
                    >
                        <Link href={href}>
                            <Icon size={18} />
                        </Link>
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="z-[9999]" sideOffset={8} avoidCollisions={true}>
                    {label}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}
